import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Pencil, Trash2, Users, DollarSign, Building2, ChevronRight, Home, FileText, Download, Loader2, Eye } from 'lucide-react';
import { format } from 'date-fns';

export default function ServicesFees() {
  const [owners, setOwners] = useState([]);
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [fees, setFees] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOwner, setSelectedOwner] = useState(null);

  // Invoice generation
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceUnit, setInvoiceUnit] = useState('');
  const [invoicePeriodStart, setInvoicePeriodStart] = useState('');
  const [invoicePeriodEnd, setInvoicePeriodEnd] = useState('');
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Owner dialog
  const [ownerDialogOpen, setOwnerDialogOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const ownerDefault = { property_id: '', unit_ids: [], owner_name: '', owner_email: '', ownership_percentage: 100, management_fee: 0, management_fee_type: 'percentage' };
  const [ownerForm, setOwnerForm] = useState(ownerDefault);

  // Fee dialog
  const [feeDialogOpen, setFeeDialogOpen] = useState(false);
  const [editingFee, setEditingFee] = useState(null);
  const feeDefault = { owner_id: '', category_name: '', amount: '', fee_type: 'flat', billing_frequency: 'per_booking', description: '' };
  const [feeForm, setFeeForm] = useState(feeDefault);

  const fetchData = async () => {
    try {
      const [o, p, u, f, s] = await Promise.all([
        api.get('/property-owners'),
        api.get('/properties'),
        api.get('/units'),
        api.get('/service-fees'),
        api.get('/service-fees/summary'),
      ]);
      setOwners(o.data);
      setProperties(p.data);
      setUnits(u.data);
      setFees(f.data);
      setSummary(s.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchData(); }, []);

  // Owner CRUD
  const handleOwnerSubmit = async (e) => {
    e.preventDefault();
    if (editingOwner) await api.put(`/property-owners/${editingOwner.id}`, ownerForm);
    else await api.post('/property-owners', ownerForm);
    setOwnerDialogOpen(false); setEditingOwner(null); setOwnerForm(ownerDefault); fetchData();
  };

  const editOwner = (o) => {
    setEditingOwner(o);
    setOwnerForm({ property_id: o.property_id || '', unit_ids: o.unit_ids || [], owner_name: o.owner_name, owner_email: o.owner_email, ownership_percentage: o.ownership_percentage || 100, management_fee: o.management_fee ?? o.management_fee_percentage ?? 0, management_fee_type: o.management_fee_type || 'percentage' });
    setOwnerDialogOpen(true);
  };

  // Fee CRUD
  const handleFeeSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...feeForm, amount: Number(feeForm.amount) };
    if (editingFee) await api.put(`/service-fees/${editingFee.id}`, payload);
    else await api.post('/service-fees', payload);
    setFeeDialogOpen(false); setEditingFee(null); setFeeForm(feeDefault); fetchData();
  };

  const editFee = (f) => {
    setEditingFee(f);
    setFeeForm({ owner_id: f.owner_id, category_name: f.category_name, amount: f.amount, fee_type: f.fee_type || 'flat', billing_frequency: f.billing_frequency || 'per_booking', description: f.description || '' });
    setFeeDialogOpen(true);
  };

  const deleteFee = async (id) => {
    if (!window.confirm('Delete this service fee?')) return;
    await api.delete(`/service-fees/${id}`); fetchData();
  };

  const addFeeForOwner = (ownerId) => {
    setEditingFee(null);
    setFeeForm({ ...feeDefault, owner_id: ownerId });
    setFeeDialogOpen(true);
  };

  // Invoice generation
  const ownerUnits = selectedOwner ? units.filter(u => {
    const ownerData = owners.find(o => o.id === selectedOwner);
    if (!ownerData) return false;
    if (ownerData.unit_ids?.length > 0) return ownerData.unit_ids.includes(u.id);
    return u.property_id === ownerData.property_id;
  }) : [];

  const openInvoiceDialog = () => {
    setInvoiceUnit('');
    setInvoicePreview(null);
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setInvoicePeriodStart(format(firstDay, 'yyyy-MM-dd'));
    setInvoicePeriodEnd(format(lastDay, 'yyyy-MM-dd'));
    setInvoiceDialogOpen(true);
  };

  const handlePreviewInvoice = async () => {
    if (!invoiceUnit || !selectedOwner) return;
    setPreviewLoading(true);
    try {
      const { data } = await api.post('/service-fee-invoice/preview', {
        owner_id: selectedOwner, unit_id: invoiceUnit, period_start: invoicePeriodStart, period_end: invoicePeriodEnd
      });
      setInvoicePreview(data);
    } catch (e) { console.error(e); }
    finally { setPreviewLoading(false); }
  };

  const handleDownloadInvoice = async () => {
    if (!invoiceUnit || !selectedOwner) return;
    setDownloadLoading(true);
    try {
      const response = await api.post('/service-fee-invoice/generate', {
        owner_id: selectedOwner, unit_id: invoiceUnit, period_start: invoicePeriodStart, period_end: invoicePeriodEnd
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ServiceFee_Invoice.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setDownloadLoading(false); }
  };

  const ownerFees = selectedOwner ? fees.filter(f => f.owner_id === selectedOwner) : [];
  const selectedOwnerData = owners.find(o => o.id === selectedOwner);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-slate-300 border-t-slate-900 rounded-full" /></div>;

  return (
    <div data-testid="services-fees-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold font-heading text-slate-900 tracking-tight">Services & Fees</h1>
          <p className="text-sm text-slate-500 mt-1">Manage owner/client details and configure service fee categories</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Owner/Client list */}
        <div className="lg:col-span-1">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Owners / Clients</h2>
            <Dialog open={ownerDialogOpen} onOpenChange={(o) => { setOwnerDialogOpen(o); if (!o) { setEditingOwner(null); setOwnerForm(ownerDefault); } }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" data-testid="add-owner-btn"><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">{editingOwner ? 'Edit Owner/Client' : 'Add Owner/Client'}</DialogTitle></DialogHeader>
                <form onSubmit={handleOwnerSubmit} className="space-y-4">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={ownerForm.owner_name} onChange={e => setOwnerForm({ ...ownerForm, owner_name: e.target.value })} required placeholder="e.g. John Smith" data-testid="owner-name-input" />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input type="email" value={ownerForm.owner_email} onChange={e => setOwnerForm({ ...ownerForm, owner_email: e.target.value })} required placeholder="john@example.com" data-testid="owner-email-input" />
                  </div>
                  <div>
                    <Label>Property</Label>
                    <Select value={ownerForm.property_id} onValueChange={v => setOwnerForm({ ...ownerForm, property_id: v, unit_ids: [] })}>
                      <SelectTrigger data-testid="owner-property-select"><SelectValue placeholder="Select property" /></SelectTrigger>
                      <SelectContent>{properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  {ownerForm.property_id && (
                    <div>
                      <Label>Units</Label>
                      <div className="mt-1.5 border border-slate-200 rounded-md p-3 max-h-40 overflow-y-auto space-y-2" data-testid="owner-units-list">
                        {units.filter(u => u.property_id === ownerForm.property_id).map(u => {
                          const unitIds = Array.isArray(ownerForm.unit_ids) ? ownerForm.unit_ids : [];
                          const checked = unitIds.includes(u.id);
                          return (
                            <label key={u.id} className="flex items-center gap-2.5 cursor-pointer hover:bg-slate-50 rounded px-1 py-0.5">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => {
                                  const current = Array.isArray(ownerForm.unit_ids) ? ownerForm.unit_ids : [];
                                  const next = e.target.checked ? [...current, u.id] : current.filter(id => id !== u.id);
                                  setOwnerForm({ ...ownerForm, unit_ids: next });
                                }}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                data-testid={`owner-unit-${u.id}`}
                              />
                              <span className="text-sm text-slate-800">{u.name}</span>
                              <span className="text-[10px] text-slate-400 ml-auto">{u.bedrooms}BR &bull; R{(u.base_price || 0).toLocaleString()}</span>
                            </label>
                          );
                        })}
                        {units.filter(u => u.property_id === ownerForm.property_id).length === 0 && (
                          <p className="text-xs text-slate-400 text-center py-2">No units for this property</p>
                        )}
                      </div>
                      {(Array.isArray(ownerForm.unit_ids) ? ownerForm.unit_ids : []).length > 0 && (
                        <p className="text-xs text-blue-600 mt-1">{ownerForm.unit_ids.length} unit{ownerForm.unit_ids.length > 1 ? 's' : ''} selected</p>
                      )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Ownership %</Label><Input type="number" value={ownerForm.ownership_percentage} onChange={e => setOwnerForm({ ...ownerForm, ownership_percentage: Number(e.target.value) })} data-testid="owner-pct-input" /></div>
                    <div>
                      <Label>Mgmt Fee</Label>
                      <div className="flex gap-1.5 mt-1.5">
                        <Input type="number" step="0.01" value={ownerForm.management_fee} onChange={e => setOwnerForm({ ...ownerForm, management_fee: Number(e.target.value) })} className="flex-1" data-testid="owner-mgmt-input" />
                        <Select value={ownerForm.management_fee_type} onValueChange={v => setOwnerForm({ ...ownerForm, management_fee_type: v })}>
                          <SelectTrigger className="w-20" data-testid="owner-mgmt-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="percentage">%</SelectItem>
                            <SelectItem value="flat">R</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="owner-submit-btn">{editingOwner ? 'Update' : 'Add Owner/Client'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="space-y-2">
            {owners.map(o => {
              const s = summary.find(x => x.owner_id === o.id);
              const isSelected = selectedOwner === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => setSelectedOwner(isSelected ? null : o.id)}
                  className={`w-full text-left bg-white border rounded-lg p-4 transition-all duration-200 ${isSelected ? 'border-blue-400 ring-1 ring-blue-200 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'}`}
                  data-testid={`owner-card-${o.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{o.owner_name}</p>
                      <p className="text-xs text-slate-500 truncate">{o.owner_email}</p>
                    </div>
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isSelected ? 'rotate-90' : ''}`} />
                  </div>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Building2 className="w-3 h-3" />{o.property_name}
                    </div>
                    {(o.unit_names || []).length > 0 && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Home className="w-3 h-3" />{o.unit_names.length} unit{o.unit_names.length > 1 ? 's' : ''}
                      </div>
                    )}
                    {s && s.fee_count > 0 && (
                      <Badge variant="outline" className="text-[10px]">{s.fee_count} fees</Badge>
                    )}
                  </div>
                  {(o.unit_names || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {o.unit_names.map((name, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded">{name}</span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-400">
                    <span>Own: {o.ownership_percentage}%</span>
                    <span>Mgmt: {o.management_fee_type === 'flat' ? `R${o.management_fee ?? o.management_fee_percentage ?? 0}` : `${o.management_fee ?? o.management_fee_percentage ?? 0}%`}</span>
                  </div>
                </button>
              );
            })}
            {owners.length === 0 && (
              <div className="text-center py-10 text-sm text-slate-400 bg-white border border-slate-200 rounded-lg">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No owners/clients yet
              </div>
            )}
          </div>
        </div>

        {/* Right: Fee categories for selected owner */}
        <div className="lg:col-span-2">
          {selectedOwner ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wider">Service Fees</h2>
                  <p className="text-xs text-slate-500 mt-0.5">for {selectedOwnerData?.owner_name}</p>
                </div>
                <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={() => addFeeForOwner(selectedOwner)} data-testid="add-fee-btn">
                  <Plus className="w-3.5 h-3.5 mr-1" />Add Fee Category
                </Button>
              </div>

              {/* Summary cards */}
              {(() => {
                const s = summary.find(x => x.owner_id === selectedOwner);
                return s ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
                    <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold font-heading text-slate-900">{s.fee_count}</p>
                      <p className="text-xs text-slate-500">Fee Categories</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold font-heading text-emerald-600">R{s.total_flat_fees.toLocaleString()}</p>
                      <p className="text-xs text-slate-500">Total Flat Fees</p>
                    </div>
                    <div className="bg-white border border-slate-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold font-heading text-blue-600">{s.total_percentage_fees}%</p>
                      <p className="text-xs text-slate-500">Total % Fees</p>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Fee list */}
              {ownerFees.length > 0 ? (
                <div className="space-y-2">
                  {ownerFees.map(fee => (
                    <div key={fee.id} className="bg-white border border-slate-200 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow" data-testid={`fee-row-${fee.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${fee.fee_type === 'percentage' ? 'bg-blue-50' : 'bg-emerald-50'}`}>
                          <DollarSign className={`w-5 h-5 ${fee.fee_type === 'percentage' ? 'text-blue-600' : 'text-emerald-600'}`} />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900">{fee.category_name}</p>
                          {fee.description && <p className="text-xs text-slate-500 truncate">{fee.description}</p>}
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-[10px]">{fee.billing_frequency === 'monthly' ? 'Monthly' : 'Per Booking'}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 shrink-0 ml-3">
                        <div className="text-right">
                          <p className={`text-lg font-bold ${fee.fee_type === 'percentage' ? 'text-blue-600' : 'text-emerald-600'}`}>
                            {fee.fee_type === 'percentage' ? `${fee.amount}%` : `R${fee.amount.toLocaleString()}`}
                          </p>
                          <Badge variant="outline" className="text-[10px]">{fee.fee_type}</Badge>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => editFee(fee)} data-testid={`edit-fee-${fee.id}`}><Pencil className="w-3 h-3" /></Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-600 hover:bg-red-50" onClick={() => deleteFee(fee.id)} data-testid={`delete-fee-${fee.id}`}><Trash2 className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-sm text-slate-400 bg-white border border-slate-200 rounded-lg border-dashed">
                  <DollarSign className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p>No service fees configured for this owner</p>
                  <Button size="sm" variant="outline" className="mt-3" onClick={() => addFeeForOwner(selectedOwner)} data-testid="add-first-fee-btn">
                    <Plus className="w-3.5 h-3.5 mr-1" />Add First Fee Category
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => editOwner(selectedOwnerData)} data-testid="edit-selected-owner-btn">
                  <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit Owner Details
                </Button>
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={openInvoiceDialog} data-testid="generate-invoice-btn">
                  <FileText className="w-3.5 h-3.5 mr-1.5" />Generate Unit Invoice
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-64 bg-white border border-slate-200 rounded-lg text-sm text-slate-400">
              <div className="text-center">
                <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
                <p>Select an owner/client to manage their service fees</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fee dialog */}
      <Dialog open={feeDialogOpen} onOpenChange={(o) => { setFeeDialogOpen(o); if (!o) { setEditingFee(null); setFeeForm(feeDefault); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{editingFee ? 'Edit Service Fee' : 'Add Service Fee'}</DialogTitle></DialogHeader>
          <form onSubmit={handleFeeSubmit} className="space-y-4">
            {!feeForm.owner_id && (
              <div>
                <Label>Owner/Client</Label>
                <Select value={feeForm.owner_id} onValueChange={v => setFeeForm({ ...feeForm, owner_id: v })}>
                  <SelectTrigger data-testid="fee-owner-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                  <SelectContent>{owners.map(o => <SelectItem key={o.id} value={o.id}>{o.owner_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Category Name</Label>
              <Input value={feeForm.category_name} onChange={e => setFeeForm({ ...feeForm, category_name: e.target.value })} required placeholder="e.g. Booking Commission, Laundry, WiFi" data-testid="fee-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input type="number" step="0.01" value={feeForm.amount} onChange={e => setFeeForm({ ...feeForm, amount: e.target.value })} required placeholder="0.00" data-testid="fee-amount-input" />
              </div>
              <div>
                <Label>Type</Label>
                <Select value={feeForm.fee_type} onValueChange={v => setFeeForm({ ...feeForm, fee_type: v })}>
                  <SelectTrigger data-testid="fee-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat (R)</SelectItem>
                    <SelectItem value="percentage">Percentage (%)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Billing Frequency</Label>
              <Select value={feeForm.billing_frequency} onValueChange={v => setFeeForm({ ...feeForm, billing_frequency: v })}>
                <SelectTrigger data-testid="fee-frequency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="per_booking">Per Booking</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={feeForm.description} onChange={e => setFeeForm({ ...feeForm, description: e.target.value })} placeholder="Additional details about this fee" data-testid="fee-desc-input" />
            </div>
            <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="fee-submit-btn">{editingFee ? 'Update Fee' : 'Add Fee Category'}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Invoice Generation Dialog */}
      <Dialog open={invoiceDialogOpen} onOpenChange={(o) => { setInvoiceDialogOpen(o); if (!o) setInvoicePreview(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">Generate Service Fee Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <p className="text-sm font-medium text-slate-900">Owner: {selectedOwnerData?.owner_name}</p>
              <p className="text-xs text-slate-500">{selectedOwnerData?.owner_email}</p>
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={invoiceUnit} onValueChange={v => { setInvoiceUnit(v); setInvoicePreview(null); }}>
                <SelectTrigger data-testid="invoice-unit-select"><SelectValue placeholder="Select unit" /></SelectTrigger>
                <SelectContent>
                  {ownerUnits.map(u => <SelectItem key={u.id} value={u.id}>{u.name} ({u.property_name})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Period Start</Label>
                <Input type="date" value={invoicePeriodStart} onChange={e => { setInvoicePeriodStart(e.target.value); setInvoicePreview(null); }} data-testid="invoice-start-input" />
              </div>
              <div>
                <Label>Period End</Label>
                <Input type="date" value={invoicePeriodEnd} onChange={e => { setInvoicePeriodEnd(e.target.value); setInvoicePreview(null); }} data-testid="invoice-end-input" />
              </div>
            </div>

            <Button variant="outline" className="w-full" onClick={handlePreviewInvoice} disabled={!invoiceUnit || previewLoading} data-testid="preview-invoice-btn">
              {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview Invoice
            </Button>

            {invoicePreview && (
              <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="invoice-preview-panel">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{invoicePreview.unit_name}</p>
                    <p className="text-xs text-slate-500">{invoicePreview.period}</p>
                  </div>
                  <Badge className="bg-blue-50 text-blue-700">{invoicePreview.num_bookings} booking{invoicePreview.num_bookings !== 1 ? 's' : ''}</Badge>
                </div>

                {/* Per-booking fee breakdowns */}
                {(invoicePreview.booking_rows || []).length > 0 && (
                  <div className="space-y-3">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Fees by Booking</p>
                    {invoicePreview.booking_rows.map((br, i) => (
                      <div key={i} className="border border-slate-100 rounded-lg overflow-hidden" data-testid={`booking-row-${i}`}>
                        <div className="bg-slate-50 px-3 py-2 flex items-center justify-between">
                          <p className="text-xs font-semibold text-slate-800">
                            {br.guest} &mdash; {br.check_in} - {br.check_out} ({br.nights} night{br.nights !== 1 ? 's' : ''})
                          </p>
                          <Badge variant="outline" className="text-[10px]">{br.source}</Badge>
                        </div>
                        <div className="px-3 py-2 space-y-1">
                          {(br.fee_items || []).map((fi, j) => (
                            <div key={j} className="flex items-center justify-between text-sm py-1 border-b border-slate-50 last:border-b-0">
                              <div className="flex items-center gap-2">
                                <span className="text-slate-700">{fi.name}</span>
                                <span className="text-[10px] text-slate-400">({fi.calc})</span>
                              </div>
                              <span className="font-medium text-slate-800">R{fi.amount.toLocaleString()}</span>
                            </div>
                          ))}
                          {(br.fee_items || []).length === 0 && (
                            <p className="text-xs text-slate-400 py-1">No fees for this booking</p>
                          )}
                        </div>
                        <div className="bg-slate-50 px-3 py-1.5 flex items-center justify-between border-t border-slate-100">
                          <span className="text-xs font-semibold text-slate-600">Booking Subtotal</span>
                          <span className="text-sm font-bold text-slate-900">R{br.fee_total.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Monthly fees */}
                {(invoicePreview.monthly_lines || []).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Monthly Fees</p>
                    {invoicePreview.monthly_lines.map((ml, i) => (
                      <div key={i} className="flex items-center justify-between text-sm py-1.5 border-b border-slate-100 last:border-b-0">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-700">{ml.name}</span>
                          <span className="text-[10px] text-slate-400">({ml.calc})</span>
                        </div>
                        <span className="font-medium text-slate-800">R{ml.amount.toLocaleString()}</span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between text-sm pt-1">
                      <span className="font-semibold text-slate-600">Monthly Subtotal</span>
                      <span className="font-bold text-slate-900">R{(invoicePreview.monthly_total || 0).toLocaleString()}</span>
                    </div>
                  </div>
                )}

                {/* Grand total */}
                <div className="bg-slate-900 rounded-lg p-4 flex items-center justify-between" data-testid="invoice-grand-total">
                  <span className="text-sm font-semibold text-white">Total Payable to Managing Agent</span>
                  <span className="text-lg font-bold text-white">R{(invoicePreview.grand_total || 0).toLocaleString()}</span>
                </div>

                {invoicePreview.num_bookings === 0 && (
                  <p className="text-xs text-amber-600 text-center">No bookings found for this period. The invoice will be empty.</p>
                )}
              </div>
            )}

            {invoicePreview && (
              <Button className="w-full bg-slate-900 hover:bg-slate-800" onClick={handleDownloadInvoice} disabled={downloadLoading} data-testid="download-invoice-btn">
                {downloadLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                Download PDF Invoice
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
